// module.helm (contract §4.8.1): HelmModule (UnderwayModules.tsx): the
// compass rose with fine ticks, a compass when the boat reports a heading and
// relative to the bow otherwise; the course over ground as a tick on the rim,
// the apparent wind as a chevron marked A and the true wind as one marked T;
// the true wind speed (else the apparent, else the speed over ground) at the
// centre; and the heading, course, speed over ground and through the water in
// the four corners, with the difference of the two speeds. Apparent-only
// boats are told so.
//
// Each turned part is a shape in <defs> drawn by a <use> bound to its figure
// (`ring`, `cog-pointer`, `awa-pointer`, `true-pointer`): the use takes the
// figure's --frac and the shape inherits it.
//
// Where the page differs from today's module: with a heading the rose is
// north-up, the heading a needle turned by `ring`, where the module turns the
// ring course-up. The pointers' figures are bearings on the ring, and a page
// cannot take one figure from another to turn them with it. Relative to the
// bow, the ring leaves out its two "90" labels, which sit on the centre
// value's line and read as a number beside it (§9.4).

import { css, h, n } from "./kit.js";
import { F } from "./module-kit.js";

const C = 110;

function polar(r, deg) {
  const a = ((deg - 90) * Math.PI) / 180;
  return { x: C + r * Math.cos(a), y: C + r * Math.sin(a) };
}

/**
 * The rose's ticks: the fine 2° ticks and the 10° minors as dashed circles
 * (a dash centred on every step, from north), and the 30° majors as one path.
 */
function ringTicks() {
  const dashes = (r, width, stepDeg, dash) => {
    const step = (2 * Math.PI * r * stepDeg) / 360;
    return {
      cx: C,
      cy: C,
      r: n(r),
      "stroke-width": n(width),
      "stroke-dasharray": `${n(dash)} ${n(step - dash)}`,
      "stroke-dashoffset": n(dash / 2),
      transform: `rotate(-90 ${C} ${C})`,
    };
  };
  let major = "";
  for (let a = 0; a < 360; a += 30) {
    const p1 = polar(87, a);
    const p2 = polar(96, a);
    major += `M${n(p1.x)} ${n(p1.y)}L${n(p2.x)} ${n(p2.y)}`;
  }
  return [
    h("circle", { class: "hr-f", ...dashes(94.75, 2.5, 2, 0.75) }),
    h("circle", { class: "hr-mi", ...dashes(93, 6, 10, 1) }),
    h("path", { class: "hr-ma", d: major }),
  ];
}

function ringLabels(relative) {
  const out = [];
  const label = (a, text, cardinal) => {
    const p = polar(74, a);
    out.push(
      h(
        "text",
        {
          class: cardinal ? "hr-c" : "hr-m",
          x: n(p.x),
          y: n(p.y + (cardinal ? 4.5 : 3.5)),
        },
        text,
      ),
    );
  };
  if (!relative) {
    for (const [a, letter] of [
      [0, "N"],
      [90, "E"],
      [180, "S"],
      [270, "W"],
    ])
      label(a, letter, true);
    for (let a = 30; a < 360; a += 30)
      if (a % 90 !== 0) label(a, String(a).padStart(3, "0"), false);
  } else {
    for (let a = -150; a <= 180; a += 30) {
      if (a === 0 || a === 180) label(a, a === 0 ? "0" : "180", true);
      else if (Math.abs(a) !== 90) label(a, String(Math.abs(a)), false);
    }
  }
  return out;
}

const chevron = (badge) => [
  h("polygon", {
    points: `${C},${C - 86} ${C + 6.5},${C - 103} ${C - 6.5},${C - 103}`,
  }),
  h("text", { class: "hr-bd", x: C, y: n(C - 95.5) }, badge),
];

export function moduleHelm() {
  const defs = h(
    "defs",
    {},
    h(
      "g",
      { id: "hr-hd" },
      h("polygon", {
        points: `${C},${C - 84} ${C + 4.5},${C - 44} ${C - 4.5},${C - 44}`,
      }),
    ),
    h(
      "g",
      { id: "hr-cog" },
      h("line", { x1: C, y1: C - 85, x2: C, y2: C - 101 }),
    ),
    h("g", { id: "hr-awa" }, chevron("A")),
    h("g", { id: "hr-tw" }, chevron("T")),
  );
  const corner = (label, field, unit, sub) =>
    h(
      "div",
      { class: "rd hr-k" },
      typeof label === "string"
        ? h("span", { class: "hr-l" }, label)
        : h("span", { class: "hr-l", ...F(label.field) }),
      h(
        "span",
        { class: "rd-row rd-sm" },
        h("span", { class: "rd-v", ...F(field) }),
        unit === null ? "" : h("span", { class: "rd-u", ...F(unit) }),
      ),
      sub === null ? "" : h("span", { class: "mo-sub hr-s", ...F(sub) }),
    );
  return {
    kind: true,
    html: [
      h("i", { class: "mo-flag", ...F("rose") }),
      h(
        "div",
        { class: "hr-box" },
        h(
          "svg",
          { class: "hr", viewBox: "0 0 220 220", "aria-hidden": "true" },
          defs,
          ringTicks(),
          h("g", { class: "hr-c0" }, ringLabels(false)),
          h("g", { class: "hr-r0" }, ringLabels(true)),
          h("use", { class: "hr-p hr-hd", href: "#hr-hd", ...F("ring") }),
          h("use", {
            class: "hr-p hr-cog",
            href: "#hr-cog",
            ...F("cog-pointer"),
          }),
          h("use", {
            class: "hr-p hr-awa",
            href: "#hr-awa",
            ...F("awa-pointer"),
          }),
          h("use", {
            class: "hr-p hr-tw",
            href: "#hr-tw",
            ...F("true-pointer"),
          }),
          h("circle", { class: "hr-disc", cx: C, cy: C, r: 54 }),
        ),
        h(
          "div",
          { class: "hr-ctr" },
          h(
            "div",
            { class: "rd" },
            h("span", { class: "hr-l", ...F("centre-label") }),
            h(
              "span",
              { class: "rd-row rd-lg" },
              h("span", { class: "rd-v", ...F("centre") }),
              h("span", { class: "rd-u", ...F("centre-unit") }),
            ),
          ),
        ),
        h("i", { class: "hr-tb", ...F("true-badge") }),
      ),
      h(
        "div",
        { class: "hr-cs" },
        corner({ field: "heading-label" }, "heading", null, null),
        corner("COG", "cog", null, null),
        corner("SOG", "sog", "sog-unit", null),
        corner("STW", "stw", "stw-unit", "delta"),
      ),
      h("p", { class: "hr-ref", ...F("reference") }),
    ],
    css: css(`
      .mo-helm { align-items: center; gap: 8px; }
      .hr-box { position: relative; width: 100%; flex: 1 1 auto; min-height: 160px; display: flex; justify-content: center; }
      .hr { display: block; height: 100%; max-width: 100%; aspect-ratio: 1; }
      .hr-f { stroke: var(--track); opacity: 0.7; fill: none; }
      .hr-mi { stroke: var(--track); fill: none; }
      .hr-ma { stroke: var(--grid); stroke-width: 1.5; fill: none; }
      .hr-c { font-size: 14px; font-weight: 600; fill: var(--muted); text-anchor: middle; }
      .hr-m { font-size: 10.5px; fill: var(--quiet); text-anchor: middle; font-variant-numeric: tabular-nums; }
      .mo-flag[data-state=relative] ~ .hr-box .hr-c0, .mo-flag:not([data-state=relative]) ~ .hr-box .hr-r0,
        .mo-flag[data-state=relative] ~ .hr-box .hr-hd { display: none; }
      .hr-hd { fill: var(--muted); }
      .hr-p { transform-origin: ${C}px ${C}px; transform: rotate(calc(var(--frac,0) * 360deg)); }
      .hr-p[data-quality=missing] { display: none; }
      .hr-cog { stroke: var(--_c); stroke-width: 3; stroke-linecap: round; }
      .hr-awa { fill: var(--tc-series-2); stroke: var(--background); stroke-width: 1.5; stroke-linejoin: round; }
      .hr-tw { fill: var(--tc-wind); stroke: var(--background); stroke-width: 1.5; stroke-linejoin: round; }
      .hr-bd { font-size: 7px; font-weight: 700; fill: var(--background); stroke: none; text-anchor: middle; }
      .hr-disc { fill: var(--background); stroke: var(--track); stroke-width: 1; }
      .hr-ctr { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; text-align: center; }
      .hr-ctr .rd { align-items: center; }
      .hr-ctr .rd-v { font-weight: 700; }
      .hr-l { font-size: 13px; font-weight: 500; color: var(--muted); white-space: nowrap; }
      .hr-tb { display: none; }
      /* The corners' numerals reach 0.15 em past their row (READOUT_CSS); the
         row pads its foot by as much, so a card drawn at its measured height
         holds them whole (§9.6, TOM-665). */
      .hr-cs { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; width: 100%; flex: none; padding-bottom: 4px; }
      .hr-k .rd-v[data-quality=missing] { color: var(--muted); }
      .hr-s { font-size: 12px; }
      .hr-ref { margin-top: 4px; font-size: 12px; color: var(--muted); align-self: flex-start; }
      .hr-ref:empty { display: none; }
      /* The underway ladder's hero cell may be handed a narrow shape: there
         the corners take two rows and the rose steps aside. Its centre
         reading stays (a value is never hidden, §9.6). */
      html[data-slot=hero] .mo-helm { container: helm / inline-size; }
      @container helm (max-width: 300px) {
        .hr-cs { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .hr-box { flex: none; min-height: 0; }
        .hr, .hr-ref { display: none; }
        .hr-ctr { position: static; }
      }
    `),
  };
}
